import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  LayoutDashboard,
  Users,
  ArrowDownToLine,
  ArrowUpFromLine,
  ReceiptText,
  Settings,
  LogOut,
  ShieldCheck,
} from "lucide-react";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "admin-nav-dashboard" },
  { to: "/admin/users", label: "Users", icon: Users, id: "admin-nav-users" },
  { to: "/admin/deposits", label: "Deposits", icon: ArrowDownToLine, id: "admin-nav-deposits" },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowUpFromLine, id: "admin-nav-withdrawals" },
  { to: "/admin/transactions", label: "Transactions", icon: ReceiptText, id: "admin-nav-transactions" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "admin-nav-settings" },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);

  const [alerts, setAlerts] = useState({ pending_deposits: 0, pending_withdrawals: 0 });
  useEffect(() => {
    let active = true;
    const fetchAlerts = () =>
      api.get("/admin/stats").then((r) => {
        if (active) setAlerts({ pending_deposits: r.data.pending_deposits, pending_withdrawals: r.data.pending_withdrawals });
      }).catch(() => {});
    fetchAlerts();
    const id = setInterval(fetchAlerts, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);
  const badgeFor = (to) => (to === "/admin/deposits" ? alerts.pending_deposits : to === "/admin/withdrawals" ? alerts.pending_withdrawals : 0);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="hidden md:flex w-64 flex-col bg-[#050506] fixed h-screen border-r border-white/10">
        <div className="px-6 py-7 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#059669]" />
            <h1 className="font-heading font-extrabold text-2xl tracking-tight text-white">
              Swiftors
            </h1>
          </div>
          <p className="overline mt-1 text-white/40">Admin Command Center</p>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#059669] text-white font-medium"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon size={17} />
              <span className="flex-1">{l.label}</span>
              {badgeFor(l.to) > 0 && (
                <span data-testid={`alert-badge-${l.label.toLowerCase()}`} className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[11px] font-bold font-mono-num">
                  {badgeFor(l.to)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-1">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-white/40 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={doLogout}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#050506] text-white flex items-center justify-between px-4 h-14 border-b border-white/10">
        <h1 className="font-heading font-extrabold text-lg">Swiftors Admin</h1>
        <button data-testid="admin-mobile-logout-btn" onClick={doLogout} className="text-white/70"><LogOut size={18} /></button>
      </div>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#050506] text-white flex justify-around py-2 border-t border-white/10">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `relative flex flex-col items-center gap-0.5 text-[9px] px-1 ${isActive ? "text-[#059669]" : "text-white/50"}`}>
            <l.icon size={18} />
            {l.label}
            {badgeFor(l.to) > 0 && (
              <span className="absolute top-0 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center">{badgeFor(l.to)}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">{children}</div>
      </main>
    </div>
  );
};
